import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AdminPageLayout } from '@/layouts/AdminPageLayout';
import { AutoSaveForm } from '@/components/forms/AutoSaveForm';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { ErrorBox } from '@/components/ui/error-box';
import { SpecialAccessUser, UserRole, AccessLevel } from '@/services/admin/specialAccessUser.interfaces';
import { getSpecialAccessUserById, updateSpecialAccessUser, resetUserPassword } from '@/services/admin/specialAccessUser.service';
import { useAppToast } from '@/hooks/useAppToast';
import { SaveIndicator } from '@/components/forms/SaveIndicator';
import { Button } from '@/components/ui/button';
import { PersonAvatar } from "@/components/ui/person-avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const roles: { value: UserRole; label: string }[] = [
  { value: 'Administrator', label: 'Administrador' },
  { value: 'Supervisor', label: 'Supervisor' },
  { value: 'Coordinator', label: 'Coordenador' },
  { value: 'Analyst', label: 'Analista' },
  { value: 'Support', label: 'Suporte' }
];

const accessLevels: { value: AccessLevel; label: string }[] = [
  { value: 'Full', label: 'Acesso Total' },
  { value: 'High', label: 'Acesso Alto' },
  { value: 'Medium', label: 'Acesso Médio' },
  { value: 'Basic', label: 'Acesso Básico' },
  { value: 'Limited', label: 'Acesso Limitado' }
];

export const EditSpecialAccessUser: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<SpecialAccessUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { success, error: showError } = useAppToast();
  const [isSaving, setIsSaving] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      if (!userId) {
        console.error('ID do usuário não fornecido');
        setError('ID do usuário não fornecido');
        setIsLoading(false);
        return;
      }

      try {
        console.log('Carregando usuário:', userId);
        const userData = await getSpecialAccessUserById(userId);
        console.log('Dados do usuário carregados:', userData);
        setUser(userData);
      } catch (err) {
        console.error('Erro ao carregar usuário:', err);
        setError('Erro ao carregar dados do usuário');
        showError('Não foi possível carregar os dados do usuário');
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadUser();
  }, [userId]);

  const handleSave = async (formData: Partial<SpecialAccessUser>) => {
    if (!userId) {
      showError('ID do usuário não fornecido');
      return;
    }

    try {
      console.log('Salvando alterações:', formData);
      await updateSpecialAccessUser(userId, formData);
      success('Alterações salvas com sucesso');
    } catch (err) {
      console.error('Erro ao salvar alterações:', err);
      showError('Erro ao salvar alterações');
      throw err;
    }
  };

  const handleBlockAccess = useCallback(async () => {
    // Implement block access logic here
    success("Access blocked successfully"); // Placeholder success message
  }, [success]);

  const handleSaveChanges = async () => {
    if (!userId || !user) return;

    try {
      setIsSaving(true);
      // Call the save API here
      success("Changes saved successfully");
    } catch (error) {
      console.error("Error saving changes:", error);
      showError("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userId) return;

    try {
      await resetUserPassword(userId);
      success("Password reset successfully");
      setResetPasswordDialogOpen(false);
    } catch (error) {
      console.error("Error resetting password:", error);
      showError("Failed to reset password");
    }
  };

  if (error) {
    return (
      <AdminPageLayout title="Editar Usuário com Acesso Especial">
        <div className="container mx-auto py-6">
          <ErrorBox message={error} />
        </div>
      </AdminPageLayout>
    );
  }

  if (isLoading || !user) {
    return (
      <AdminPageLayout title="Editar Usuário com Acesso Especial">
        <div className="container mx-auto py-6">
          <Card className="p-6">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <i className="fa-solid fa-circle-notch fa-spin text-2xl text-neutral-400 mb-2"></i>
                <p>Carregando dados do usuário...</p>
              </div>
            </div>
          </Card>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title={`Editar Perfil de ${user.name}`}>
      <main id="main-content" className="mx-auto py-8">
        <div id="student-header" className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <PersonAvatar
              size="xl"
              userId={userId}
              userName={user.name}
              avatarUrl={user.avatar?.url}
            />
            <div>
              <h1 className="text-2xl font-semibold">{user.name}</h1>
              <p className="text-sm text-neutral-500">ID: {userId}</p>
            </div>
          </div>
          <Button variant="outline" className="space-x-2">
            <i className="fa-solid fa-message"></i>
            <span>Enviar Mensagem</span>
          </Button>
        </div>
        <AutoSaveForm onSave={handleSave}>
          <div id="student-form" className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
            <div className="mb-4 flex justify-end">
              <SaveIndicator status={{ status: 'idle' }} />
            </div>
            <div className="grid grid-cols-2 gap-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={user.name}
                  required
                  maxLength={100}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  defaultValue={user.email}
                  required
                />
              </div>

              {/* Função/Role */}
              <div className="space-y-2">
                <Label htmlFor="role">Função</Label>
                <Select name="role" defaultValue={user.role}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma função" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Nível de Acesso */}
              <div className="space-y-2">
                <Label htmlFor="accessLevel">Nível de Acesso</Label>
                <Select name="accessLevel" defaultValue={user.accessLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um nível" />
                  </SelectTrigger>
                  <SelectContent>
                    {accessLevels.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select name="status" defaultValue={user.status}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Ativo</SelectItem>
                    <SelectItem value="Inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Informações somente leitura */}
            <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t">
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <i className="fa-solid fa-clock text-neutral-500"></i>
                  <span>Último Login</span>
                </Label>
                <Input value={user.lastLogin} readOnly className="bg-neutral-50" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <i className="fa-regular fa-calendar text-neutral-500"></i>
                  <span>Data de Registro</span>
                </Label>
                <Input value={user.enrollmentDate} readOnly className="bg-neutral-50" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <i className="fa-solid fa-key text-neutral-500"></i>
                  <span>Alterar Senha</span>
                </Label>
                <Button
                  variant="outline"
                  className="w-full justify-start space-x-2"
                  onClick={() => setResetPasswordDialogOpen(true)}
                  type="button"
                >
                  <i className="fa-solid fa-key"></i>
                  <span>Resetar Senha</span>
                </Button>
                {user.lastPasswordReset ? (
                  <p className="text-sm text-neutral-500">
                    Última redefinição: {new Date(user.lastPasswordReset).toLocaleDateString()}
                  </p>
                ) : (
                  <p className="text-sm text-neutral-500">Nunca redefinida</p>
                )}
              </div>
            </div>
          </div>
        </AutoSaveForm>
        <div id="actions" className="mt-8 flex items-center justify-between pt-6 border-t">
          <div className="flex space-x-4">
            <Button variant="outline" className="space-x-2" onClick={() => handleBlockAccess()}>
              <i className="fa-solid fa-ban"></i>
              <span>Bloquear Acesso</span>
            </Button>
          </div>
          <div className="flex space-x-4">
            <Button variant="outline" onClick={() => window.history.back()}>Cancelar</Button>
            <Button variant="default" onClick={handleSaveChanges} disabled={isSaving}>
              {isSaving ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </div>
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Redefinir Senha</DialogTitle>
              <DialogDescription>
                Tem certeza de que deseja redefinir a senha deste usuário? Ele receberá um email com uma senha temporária.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleResetPassword}>
                Redefinir Senha
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AdminPageLayout>
  );
};

export default EditSpecialAccessUser;
